import {
    ActivityModality,
    CourseProps,
    EvaluationStatus,
    LearningActivityStatus,
    RegistrationStatus,
    TranscriptStatus,
    VILTType,
} from './types';
import { ApplicationUser } from '../hooks/auth/useAuth';

import { isCourse2HoursBeforeStartTime, isAfterClassStartTime, isBeforeClassEndTime, isWithin15MinutesOfStart } from './utils';
import { rules } from './actionButtonRules/rules';
import { getActionButtonFromRules } from './actionButtonRules/actionButtonResolver';
import { FeatureFlagsCondition } from './actionButtonRules/conditions';
import { ActionButton } from './actionButtonRules/types';

const isRegistered = (course: CourseProps): boolean => {
    return course.registrationStatus === RegistrationStatus.REGISTERED;
};

const getActionButton = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): ActionButton => {
    return getActionButtonFromRules(rules, course, user, features, requestingAccessToOrgId, userRequests);
};

export const shouldShowEnrollButton = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.ENROLL;
};
export const shouldShowWithdrawButton = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.WITHDRAW;
};

export const shouldShowWaitlistButton = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.JOIN_WAITLIST;
};

export const shouldShowUnwaitlistButton = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.UN_WAITLIST;
};

export const shouldShowJoinWebExTrainingButton = (course: CourseProps, features?: FeatureFlagsCondition): boolean => {
    const isCourseActive = course.courseStatus === LearningActivityStatus.ACTIVE;

    return (
        course.activityModality === ActivityModality.vILT &&
        !(features?.hideJoinButtonForNonWebExCourse && course.vIltType != VILTType.WebEx) &&
        isRegistered(course) &&
        isWithin15MinutesOfStart(course) &&
        isBeforeClassEndTime(course) &&
        isCourseActive
    );
};

export const shouldShowJoinInstruction = (course: CourseProps, features?: FeatureFlagsCondition): boolean => {
    const isCourseActive = course.courseStatus === LearningActivityStatus.ACTIVE;

    return (
        (features?.hideJoinButtonForNonWebExCourse ?? false) &&
        course.activityModality === ActivityModality.vILT &&
        course.vIltType !== VILTType.WebEx &&
        isRegistered(course) &&
        isWithin15MinutesOfStart(course) &&
        isBeforeClassEndTime(course) &&
        isCourseActive
    );
};

export const shouldShowClassroomButton = (course: CourseProps): boolean => {
    return isCourse2HoursBeforeStartTime(course) && isRegistered(course) && !!course.classroomUrl;
};

export const shouldShowEvaluationButton = (course: CourseProps): boolean => {
    return (
        isAfterClassStartTime(course) &&
        isRegistered(course) &&
        !!course.evaluationUrl &&
        course.evaluationStatus === EvaluationStatus.INCOMPLETE
    );
};

export const shouldShowCertificateButton = (course: CourseProps): boolean => {
    return isAfterClassStartTime(course) && isRegistered(course) && course.transcriptStatus === TranscriptStatus.ATTENDED;
};

export const shouldShowRequestAccessButton = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.REQUEST_ACCESS;
};

export const shouldShowRequestAccessPending = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.REQUEST_ACCESS_PENDING;
};

export const shouldShowRequestAccessApproved = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.REQUEST_ACCESS_APPROVED;
};

export const shouldShowRequestAccessRejected = (
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): boolean => {
    return getActionButton(course, user, features, requestingAccessToOrgId, userRequests) === ActionButton.REQUEST_ACCESS_REJECTED;
};
